import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PRICE_LABEL } from "@/lib/markets";
import BrandLogo from "@/components/BrandLogo";

const LINKS = [
  ["The Edge", "benefits"],
  ["The Room", "room"],
  ["Why Join", "why-join"],
  ["Voices", "testimonials"],
  ["Reviews", "reviews"],
  ["Manifesto", "manifesto"],
];

const Nav = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    if (id === "reviews") {
      navigate("/reviews");
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    navigate("/");
    window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  return (
    <header data-testid="main-nav" className="fixed inset-x-0 top-4 z-50 px-4">
      <div className="glass mx-auto flex max-w-6xl items-center justify-between rounded-full px-5 py-3 sm:px-6">
        <button
          data-testid="nav-brand"
          onClick={() => {
            setOpen(false);
            navigate("/");
          }}
          className="flex items-center gap-2.5"
        >
          <BrandLogo className="h-8 sm:h-9" />
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map(([label, id]) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-slate-500 transition-colors hover:text-[#0a0a0a]"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden items-center gap-2 font-mono2 text-[11px] uppercase tracking-widest text-[#15803d] sm:flex">
            <span className="anim-pulse-dot h-2 w-2 rounded-full bg-[#16a34a]" />
            Rooms Open
          </span>
          <button
            data-testid="nav-vip-cta"
            onClick={() => navigate("/checkout")}
            className="btn-main whitespace-nowrap rounded-full px-4 py-2 font-sub text-xs font-semibold uppercase tracking-wide sm:px-5 sm:py-2.5 sm:text-sm"
          >
            Join · {PRICE_LABEL.split(" ·")[0]}
          </button>
          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-[#0a0a0a] transition-colors hover:bg-black/5 md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="glass mx-auto mt-3 max-w-6xl rounded-3xl p-3 md:hidden"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map(([label, id], i) => (
              <motion.button
                key={id}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.3 }}
                data-testid={`nav-mobile-link-${id}`}
                onClick={() => go(id)}
                className="flex w-full items-center justify-between rounded-2xl px-5 py-4 text-left font-sub text-sm font-semibold uppercase tracking-wide text-slate-700 transition-colors hover:bg-black/5 hover:text-[#0a0a0a]"
              >
                {label}
                <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-400">0{i + 1}</span>
              </motion.button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
